import { Signal, computed, signal } from "@preact/signals-core";

export interface FieldErrors {
  [field: string]: string | undefined;
}

export interface Form<T extends Record<string, unknown>> {
  fields: { [K in keyof T]: Signal<T[K]> };
  errors: Signal<FieldErrors>;
  dirty: Signal<boolean>;
  reset(values?: T): void;
  setServerErrors(fieldErrors: FieldErrors): void;
  values(): T;
}

/** One signal per field, plus dirty tracking against the initial snapshot and a slot for API field errors. */
export function form<T extends Record<string, unknown>>(initial: T): Form<T> {
  let initialValues = { ...initial };
  const keys = Object.keys(initial) as (keyof T)[];

  const fields = {} as { [K in keyof T]: Signal<T[K]> };
  for (const key of keys) fields[key] = signal(initial[key]);

  const errors = signal<FieldErrors>({});
  const dirty = computed(() => keys.some((key) => fields[key].value !== initialValues[key]));

  function reset(values: T = initialValues): void {
    initialValues = { ...values };
    for (const key of keys) fields[key].value = values[key];
    errors.value = {};
  }

  function setServerErrors(fieldErrors: FieldErrors): void {
    errors.value = fieldErrors;
  }

  function values(): T {
    const result = {} as T;
    for (const key of keys) result[key] = fields[key].value;
    return result;
  }

  return { fields, errors, dirty, reset, setServerErrors, values };
}
